import type React from 'react'
import { createContext, useEffect, useState } from 'react'

const THEME_STORAGE_KEY = 'kingdom-theme'

type Theme = 'light' | 'dark'

const getInitialTheme = (): Theme => {
  if (typeof window === 'undefined') {
    return 'light'
  }

  const stored = localStorage.getItem(THEME_STORAGE_KEY)
  if (stored === 'light' || stored === 'dark') {
    return stored
  }

  if (window.matchMedia?.('(prefers-color-scheme: dark)').matches) {
    return 'dark'
  }

  return 'light'
}

interface ContextProps {
  darkTheme: boolean
  toggleTheme: () => void
}

export const ThemeContext = createContext<ContextProps>({
  darkTheme: false,
  toggleTheme: () => {}
})

interface Props {
  children?: React.ReactNode
}

const ThemeContextProvider: React.FC<Props> = ({ children }) => {
  const [darkTheme, setDarkTheme] = useState<boolean>(() => getInitialTheme() === 'dark')

  useEffect(() => {
    const theme: Theme = darkTheme ? 'dark' : 'light'
    document.documentElement.setAttribute('data-theme', theme)
    localStorage.setItem(THEME_STORAGE_KEY, theme)
  }, [darkTheme])

  const toggleThemeHandler = () => {
    setDarkTheme(prevState => !prevState)
  }

  return (
    <ThemeContext.Provider
      value={{
        darkTheme: darkTheme,
        toggleTheme: toggleThemeHandler
      }}
    >
      {children}
    </ThemeContext.Provider>
  )
}

export default ThemeContextProvider
